import { Injectable, NgZone } from '@angular/core';
import { Actions, Effect, toPayload } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import {
  bufferTime,
  filter,
  map,
  mergeAll,
  mergeMap,
  observeOn,
} from 'rxjs/operators';
import { async } from 'rxjs/scheduler/async';
import { RequestDataPayload, TypedAction } from './actions';
import { ApiHttp } from './api-http.service';
import { PathList, referenceListsEqual, uniq } from './helpers';
import { enterZone, leaveZone } from './zone-util';

function batch(requests: RequestDataPayload<any>[]): RequestDataPayload<any>[] {
  const batches: { [key: string]: RequestDataPayload<any> } = {};
  let finishedBatches: RequestDataPayload<any>[] = [];

  requests.forEach(r => {
    if (r.refList.query) {
      finishedBatches = [...finishedBatches, r];
    } else if (batches[r.type] === undefined) {
      batches[r.type] = r;
    } else {
      const pathList: PathList<any> = [
        r.refList.pathList[0],
        [...batches[r.type].refList.pathList[1], ...r.refList.pathList[1]],
      ];
      const payload = { type: r.type, refList: { pathList } };
      if (pathList[1].length > 1000) {
        finishedBatches = [...finishedBatches, payload];
        delete batches[r.type];
      } else {
        batches[r.type] = payload;
      }
    }
  });

  return [...finishedBatches, ...Object.values(batches)];
}

// TODO type refs/uniq back properly
@Injectable()
export class RequestDataEffect {
  @Effect()
  requestData: Observable<TypedAction> = this.actions
    .ofType('REQUEST_DATA')
    .pipe(
      map<TypedAction, RequestDataPayload<any>>(toPayload),
      bufferTime(250, leaveZone(this.ngZone, async)),
      filter(requests => requests.length > 0),
      observeOn(enterZone(this.ngZone, async)),
      map(requests => {
        const uniqRequests = uniq(requests, (a, b) =>
          referenceListsEqual(a.refList, b.refList),
        );
        return <any>batch(uniqRequests);
      }), // TODO: https://github.com/ReactiveX/rxjs/issues/2759
      mergeAll<RequestDataPayload<any>>(),
      mergeMap(
        request => this.apiHttp.fetch(request.refList),
        (request, res) => ({ res, request }),
      ),
      map(({ res, request }) => {
        switch (res.status) {
          case 'success': {
            const action: TypedAction = {
              type: 'API_FETCH_SUCCESS',
              payload: {
                type: request.refList.pathList[0],
                result: res.result,
                refList: request.refList,
              },
            };
            return action;
          }
          case 'error': {
            const action: TypedAction = {
              type: 'API_FETCH_ERROR',
              payload: {
                type: request.refList.pathList[0],
                message: res.message,
                refList: request.refList,
              },
            };
            return action;
          }
          default: {
            const action: TypedAction = {
              type: 'UNKNOWN_API_FETCH_ERROR',
              payload: {
                type: request.refList.pathList[0],
                message: res,
                refList: request.refList,
              },
            };
            return action;
          }
        }
      }),
    );

  constructor(
    private apiHttp: ApiHttp,
    private actions: Actions<TypedAction>,
    private ngZone: NgZone,
  ) {}
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/request-data.effect.ts